import React, { useState } from 'react';
import apiService from '../services/api';

const PetSetup = ({ onComplete, onBackToHome, clientId }) => {
  const [pets, setPets] = useState([{ name: '', age: '', breed: '', target_behaviors: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePetChange = (index, field, value) => {
    const updatedPets = [...pets];
    updatedPets[index][field] = value;
    setPets(updatedPets);
  };

  const addPet = () => {
    setPets([...pets, { name: '', age: '', breed: '', target_behaviors: '' }]);
  };

  const removePet = (index) => {
    if (pets.length > 1) {
      const updatedPets = pets.filter((_, i) => i !== index);
      setPets(updatedPets);
    }
  };

  const validatePets = () => {
    for (let i = 0; i < pets.length; i++) {
      const pet = pets[i];
      if (!pet.name.trim()) {
        setError(`Pet ${i + 1}: Name is required`);
        return false;
      }
      if (!pet.age || pet.age < 0 || pet.age > 30) {
        setError(`Pet ${i + 1}: Please enter a valid age (0-30 years)`);
        return false;
      }
      if (!pet.breed.trim()) {
        setError(`Pet ${i + 1}: Breed is required`);
        return false;
      }
      if (!pet.target_behaviors.trim()) {
        setError(`Pet ${i + 1}: Target behaviors are required`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePets()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Save each pet
      for (const pet of pets) {
        await apiService.createPet({
          client_id: clientId,
          pet_name: pet.name,
          pet_age: parseInt(pet.age),
          pet_breed: pet.breed,
          target_behaviors: pet.target_behaviors
        });
      }

      // Redirect to client portal
      onComplete();
    } catch (err) {
      console.error('Pet setup error:', err);
      setError(err.message || 'Failed to save pet information');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    formContainer: {
      background: 'white',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    logo: {
      width: '60px',
      height: '60px',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.5rem',
      margin: '0 auto 15px auto'
    },
    title: {
      fontSize: '1.8rem',
      fontWeight: '700',
      color: '#2c3e50',
      marginBottom: '10px'
    },
    subtitle: {
      color: '#7f8c8d',
      fontSize: '1rem',
      lineHeight: '1.5'
    },
    backButton: {
      background: 'transparent',
      border: 'none',
      color: '#7f8c8d',
      fontSize: '1rem',
      cursor: 'pointer',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    petSection: {
      marginBottom: '30px',
      padding: '20px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      backgroundColor: '#f8f9fa'
    },
    petHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    petTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#2c3e50'
    },
    removeButton: {
      background: '#e74c3c',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      cursor: 'pointer',
      fontSize: '1.2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 15px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '1rem',
      boxSizing: 'border-box'
    },
    addPetButton: {
      background: 'transparent',
      border: '2px dashed #e67e22',
      color: '#e67e22',
      padding: '20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '600',
      width: '100%',
      marginBottom: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    submitButton: {
      width: '100%',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      padding: '15px',
      borderRadius: '8px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'transform 0.2s ease'
    },
    errorMessage: {
      color: '#e74c3c',
      fontSize: '0.9rem',
      marginBottom: '20px',
      padding: '10px',
      backgroundColor: '#fdf2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        <button
          onClick={onBackToHome}
          style={styles.backButton}
        >
          ← Back to Home
        </button>

        <div style={styles.header}>
          <div style={styles.logo}>
            <img 
              src="/images/logo.png" 
              alt="Pheefee Logo" 
              style={{width: '40px', height: '40px', objectFit: 'contain'}}
            />
          </div>
          <h1 style={styles.title}>
            Tell Us About Your Pets
          </h1>
          <p style={styles.subtitle}>
            Add information about your dogs so trainers can provide the best care
          </p>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {pets.map((pet, index) => (
            <div key={index} style={styles.petSection}>
              <div style={styles.petHeader}>
                <h3 style={styles.petTitle}>
                  🐕 Pet {index + 1}
                </h3>
                {pets.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePet(index)}
                    style={styles.removeButton}
                  >
                    ×
                  </button>
                )}
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Pet Name *
                </label>
                <input
                  type="text"
                  value={pet.name}
                  onChange={(e) => handlePetChange(index, 'name', e.target.value)}
                  placeholder="e.g., Buddy, Luna, Max"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Age (years) *
                </label>
                <input
                  type="number"
                  value={pet.age}
                  onChange={(e) => handlePetChange(index, 'age', e.target.value)}
                  placeholder="e.g., 3"
                  min="0"
                  max="30"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Breed *
                </label>
                <input
                  type="text"
                  value={pet.breed}
                  onChange={(e) => handlePetChange(index, 'breed', e.target.value)}
                  placeholder="e.g., Golden Retriever, Mixed, German Shepherd"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Target Behaviors *
                </label>
                <textarea
                  value={pet.target_behaviors}
                  onChange={(e) => handlePetChange(index, 'target_behaviors', e.target.value)}
                  placeholder="e.g., Basic obedience, house training, socialization, leash walking, etc."
                  style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                  required
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addPet}
            style={styles.addPetButton}
          >
            + Add Another Pet
          </button>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            onMouseOver={(e) => {}}
            onMouseOut={(e) => {}}
          >
            {loading ? 'Saving...' : 'Finish Setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PetSetup;
